// React
import React, { ReactElement, useEffect, useState } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Router
import { useRouter } from 'next/router';
// Utils
import { getGender } from '@utmb/utils/gender';
// Design System
import { Flag } from '@utmb/design-system/flag/Flag';
import { Link } from '@utmb/design-system/link/link';
import { MyTable } from '@utmb/design-system/my-table/MyTable';
import { MyTableCell } from '@utmb/design-system/my-table/MyTableCell';
import { MyTableHeader } from '@utmb/design-system/my-table/MyTableHeader';
import { MyTableRow } from '@utmb/design-system/my-table/MyTableRow';
import { Pagination } from '@utmb/design-system/pagination/Pagination';
// Interfaces
import RaceRegistrationView from '@utmb/types/view/RaceRegistrationView';
// Styles
import styles from './start-list-table.module.scss';
import { SliceStartListView } from '@utmb/types/index';
import { useCountry } from '@utmb/contexts/CountryContext';
import RaceRegistrationPaginatedView from '@utmb/types/view/RaceRegistrationPaginatedView';
import { fetchTheAPI } from '@utmb/utils/utmb';
import { Font } from '@utmb/design-system/text/font';
import { useTenancy } from '@utmb/contexts/TenancyContext';
import NationalityFilter from '@utmb/features/start-list/filters/NationalityFilter';
import GenderFilter from '@utmb/features/start-list/filters/GenderFilter';
import { SexEnum } from '@utmb/types/enum/SexEnum';
import RacesFilter from '@utmb/features/start-list/filters/RacesFilter';
import SearchFilter from '@utmb/features/start-list/filters/SearchFilter';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import { useUser } from '@utmb/contexts/AuthContext';

export const StartList = (props: SliceStartListView): ReactElement => {
    const { registrationPaginatedView, filters } = props;

    const { isLoginInProgress } = useUser();
    const { tenantId } = useTenancy();
    const router = useRouter();
    const { locale, push, pathname } = router;
    const { t } = useTranslation();
    const [regPageView, setRegPageView] = useState<RaceRegistrationPaginatedView>(registrationPaginatedView);
    const [isLoading, setIsLoading] = useState(false);
    const [isInit, setIsInit] = useState(false);
    const [currentNationality, setCurrentNationality] = useState<string>(router.query.nationality ? `${router.query.nationality}` : '');
    const [currentGender, setCurrentGender] = useState<SexEnum | string>(router.query.gender ? `${router.query.gender}` : '');
    const [currentRaces, setCurrentRaces] = useState<string>(router.query.races ? `${router.query.races}` : '');
    const [currentSearch, setCurrentSearch] = useState<string>(router.query.search ? `${router.query.search}` : '');
    const [searchButtonActive, setSearchButtonActive] = useState<boolean>(false);

    const { countries, initCountries } = useCountry();
    useEffect(() => {
        if (!isLoginInProgress) {
            initCountries();
            if ((currentGender || currentSearch || currentRaces || currentNationality) && !isInit) {
                setIsInit(true);
                getRegistrations(false).then();
            }
        }
    }, [isLoginInProgress]);

    const options = countries
        .filter((country) => filters.cioCodes.includes(country.cio))
        .map((countryView) => ({
            label: countryView.name,
            value: countryView.code,
        }));

    let offset = regPageView?.offset ?? 0;
    const limit = regPageView?.limit ?? 25;
    let currentPage = offset / limit;

    if (isNaN(currentPage)) {
        currentPage = 0;
    }

    const totalPages = Math.ceil(regPageView?.nbHits / regPageView?.limit);

    const hasBib = !!regPageView?.registrations?.find((reg) => reg.bib);

    async function getRegistrations(reinitPage = true): Promise<void> {
        setIsLoading(true);
        let racesString = filters.races.map((raceView) => raceView.strapiId.toString()).join(',');

        if (reinitPage) {
            router.query.page = '1';
        }
        if (router.query.page) {
            const page = Number(router.query.page);
            if (page > 1) {
                offset = (page - 1) * Number(limit);
            } else {
                offset = 0;
            }
        }

        if (currentSearch) {
            router.query.search = currentSearch;
        } else {
            delete router.query.search;
        }

        if (currentGender) {
            router.query.gender = currentGender;
        } else {
            delete router.query.gender;
        }

        if (currentNationality) {
            router.query.nationality = currentNationality;
        } else {
            delete router.query.nationality;
        }

        if (currentRaces) {
            router.query.races = currentRaces;
            racesString = currentRaces;
        } else {
            delete router.query.races;
        }

        await router.push(
            {
                pathname: pathname,
                query: { ...router.query },
            },
            undefined,
            { shallow: true }
        );

        const registrations = (await fetchTheAPI({
            tenantId,
            path: `/registration/internal/${racesString}`,
            params: {
                offset: offset.toString(),
                limit: limit.toString(),
                search: router.query?.search?.toString() ?? '',
                gender: router.query?.gender?.toString() ?? '',
                nationality: router.query?.nationality?.toString() ?? '',
                showFullList: `${filters.showFullList}`,
            },
        })) as RaceRegistrationPaginatedView;

        setRegPageView(registrations);
        setSearchButtonActive(false);
        setIsLoading(false);
    }

    const hrefBuilder = (page: number, pageCount: number) => (page >= 1 && page <= pageCount ? `/` + pathname + `?page=${page}` : '#');

    const handlePageChange = async (event: { selected: number }): Promise<void> => {
        const page = event.selected + 1;
        router.query.page = page.toString();
        push(
            {
                pathname: pathname,
                query: { ...router.query, page },
            },
            undefined,
            { shallow: true }
        );

        getRegistrations(false).then();
    };

    useEffect(() => {
        setSearchButtonActive(true);
    }, [currentSearch, currentNationality, currentRaces, currentGender]);

    if (isLoginInProgress) return <></>;

    const arrayEmptyRows = [...Array(5)];

    return (
        <section className={`${styles.rt_root} container g-0`}>
            <div className={styles.rt_table_container}>
                <div className={styles.filter_container}>
                    <div className={styles.filter_subContainer}>
                        <SearchFilter currentSearch={currentSearch} setCurrentSearch={setCurrentSearch} />
                    </div>
                    <div className={styles.filter_subContainer}>
                        <NationalityFilter
                            nationalities={countries.filter((country) => filters.cioCodes.includes(country.cio))}
                            selectOptions={options}
                            currentNationality={currentNationality}
                            setCurrentNationality={setCurrentNationality}
                        />
                    </div>
                    <div className={styles.filter_subContainer}>
                        <GenderFilter currentGender={currentGender} setCurrentGender={setCurrentGender} />
                    </div>
                    <div className={styles.filter_subContainer}>
                        <RacesFilter races={filters.races} currentRaces={currentRaces} setCurrentRaces={setCurrentRaces} />
                    </div>
                    <div className={styles.filter_subContainer_search}>
                        <button
                            className={[styles.searchButton, searchButtonActive ? styles.searchButtonActive : styles.searchButtonInactive].join(' ')}
                            onClick={() => getRegistrations()}
                        >
                            <Font.OxaniumMedium mobile="14" desktop="14">
                                {t('qualifier.filters.search')}
                            </Font.OxaniumMedium>
                        </button>
                    </div>
                </div>
                <div className={styles.nbRegistered}>
                    {regPageView?.statusCode == 413 ? (
                        <Font.OxaniumBold className={styles.hit_qty} mobile="16" desktop="18" as="h2">
                            {t('registeredList.tooLarge')}
                        </Font.OxaniumBold>
                    ) : (
                        <Font.OxaniumBold className={styles.hit_qty} mobile="16" desktop="18" as="h2">
                            {regPageView?.nbHits
                                ? `${regPageView?.nbHits} ${t(`registeredList.result.${regPageView?.nbHits > 1 ? 'plural' : 'singular'}`)}`
                                : t('registeredList.noResult')}
                        </Font.OxaniumBold>
                    )}
                </div>
                {regPageView?.statusCode != 413 && (regPageView?.registrations?.length ?? 0) > 0 ? (
                    <MyTable className={styles.rt_table}>
                        <MyTableHeader height={65}>
                            {hasBib ? <MyTableCell column={1}>{t('common.bib')}</MyTableCell> : <></>}
                            <MyTableCell column={4}>{t('common.fullname')}</MyTableCell>
                            <MyTableCell column={2} hiddenOnMobile>
                                {t('raceRanking.tableHeader.nationality')}
                            </MyTableCell>
                            <MyTableCell column={1} hiddenOnMobile>
                                {t('common.gender.label')}
                            </MyTableCell>
                            <MyTableCell column={2}>{t('registeredList.race')}</MyTableCell>
                        </MyTableHeader>
                        {!isLoading ? (
                            <>
                                {regPageView?.registrations?.map((reg: RaceRegistrationView, indexKey: number) => {
                                    const country = countries?.find((country) => country.cio === reg.nationalityCode);
                                    const raceInfo = filters.races?.find((raceView) => raceView.raceView.name === reg.raceName);

                                    return (
                                        <MyTableRow key={indexKey} height={40}>
                                            {hasBib && <MyTableCell column={1}>{reg.bib}</MyTableCell>}
                                            <MyTableCell column={4}>
                                                {reg.runnerUri ? (
                                                    <Link href={`https://utmb.world/${locale}/runner/${reg.runnerUri}`}>{reg.fullname}</Link>
                                                ) : (
                                                    reg.fullname
                                                )}
                                            </MyTableCell>
                                            <MyTableCell column={2} hiddenOnMobile>
                                                {country ? (
                                                    <div className={styles.contentColumn}>
                                                        <Flag isoCode={country.code} /> <span>{country.name}</span>
                                                    </div>
                                                ) : (
                                                    <span>{reg.nationalityCode}</span>
                                                )}
                                            </MyTableCell>
                                            <MyTableCell column={1} hiddenOnMobile>
                                                {t(getGender(reg.gender))}
                                            </MyTableCell>
                                            <MyTableCell column={2}>
                                                <div className={styles.contentColumn}>
                                                    <PiCategoryLogo height={20} piCategory={raceInfo?.raceView.category as PiCategoryEnum} />{' '}
                                                    {raceInfo?.raceView.name}
                                                </div>
                                            </MyTableCell>
                                        </MyTableRow>
                                    );
                                })}
                            </>
                        ) : (
                            <>
                                {arrayEmptyRows.map((_, index) => (
                                    <MyTableRow key={index} height={40}>
                                        <MyTableCell column={4}>
                                            <div className={styles.loadingCell} />
                                        </MyTableCell>
                                        <MyTableCell column={2} hiddenOnMobile>
                                            <div className={styles.loadingCell} />
                                        </MyTableCell>
                                        <MyTableCell column={1} hiddenOnMobile>
                                            <div className={styles.loadingCell} />
                                        </MyTableCell>
                                        <MyTableCell column={2}>
                                            <div className={styles.loadingCell} />
                                        </MyTableCell>
                                    </MyTableRow>
                                ))}
                            </>
                        )}
                    </MyTable>
                ) : (
                    <></>
                )}
                {totalPages > 1 && <Pagination forcePage={currentPage} PageCount={totalPages} onPageChange={handlePageChange} hrefBuilder={hrefBuilder} />}
            </div>
        </section>
    );
};
